// Validation helpers for the Custom Attributes drawer.
//
//   isFieldValid(field, value)       → { ok: bool, msg: string|null }
//   isSectionComplete(cfg, sectionId, values)
//   isAllValid(cfg, values)          → true only if every mandatory field passes

import { fieldsBySection } from "./normalize";

function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "string") return v.trim() === "";
  return false;
}

export function isFieldValid(field, value) {
  const mandatory = field.mandatory === "Y";
  if (mandatory && isEmpty(value)) return { ok: false, msg: `${field.name} is required` };

  switch (field.type) {
    case "MCH": {
      const arr = value || [];
      if (field.minSelect && arr.length < field.minSelect)
        return { ok: false, msg: `Select at least ${field.minSelect}` };
      if (field.maxSelect && arr.length > field.maxSelect)
        return { ok: false, msg: `Select at most ${field.maxSelect}` };
      const allowed = new Set((field.options || []).map((o) => o.value));
      for (const v of arr) if (!allowed.has(v)) return { ok: false, msg: `Invalid option: ${v}` };
      return { ok: true, msg: null };
    }
    case "CHP":
    case "TGL":
    case "RDC":
    case "LST": {
      if (isEmpty(value)) return { ok: !mandatory, msg: null };
      const allowed = new Set((field.options || []).map((o) => o.value));
      return allowed.has(value) ? { ok: true, msg: null } : { ok: false, msg: `Invalid option: ${value}` };
    }
    case "NUM":
    case "QTY": {
      if (isEmpty(value)) return { ok: !mandatory, msg: null };
      const n = Number(value);
      if (Number.isNaN(n)) return { ok: false, msg: `${field.name} must be a number` };
      if (field.min != null && n < field.min) return { ok: false, msg: `Min ${field.min}` };
      if (field.max != null && n > field.max) return { ok: false, msg: `Max ${field.max}` };
      return { ok: true, msg: null };
    }
    case "STR":
    case "TXT": {
      if (isEmpty(value)) return { ok: !mandatory, msg: null };
      if (field.maxLength && String(value).length > field.maxLength)
        return { ok: false, msg: `Max length ${field.maxLength}` };
      if (field.pattern) {
        try {
          const re = new RegExp(field.pattern);
          if (!re.test(String(value))) return { ok: false, msg: "Invalid format" };
        } catch {
          /* bad pattern — ignore */
        }
      }
      return { ok: true, msg: null };
    }
    case "UPL":
    case "MIM":
    case "IMG": {
      const arr = Array.isArray(value) ? value : [];
      if (field.maxFiles && arr.length > field.maxFiles)
        return { ok: false, msg: `Max ${field.maxFiles} images` };
      return { ok: true, msg: null };
    }
    case "ADG":
    case "GAL":
    case "AUD":
    case "DAT":
    case "DTT":
    case "YEN":
    case "IMG":
    default:
      return { ok: true, msg: null };
  }
}

export function isSectionComplete(cfg, sectionId, values) {
  const fields = fieldsBySection(cfg, sectionId);
  return fields.every((f) => isFieldValid(f, values[f.name]).ok);
}

export function sectionHasRequired(cfg, sectionId) {
  return fieldsBySection(cfg, sectionId).some((f) => f.mandatory === "Y");
}

export function isAllValid(cfg, values) {
  return (cfg.fields || []).every((f) => isFieldValid(f, values[f.name]).ok);
}
